import React, { useEffect, useState, useRef } from "react";
import { Route, Link } from "react-router-dom";
import { motion } from "framer-motion";
import useFetch from "../../components/API/useFetch";

import styles from "./withSection.module.css";

import AnimatedTitleContainer from "../AnimatedTitle";
import GifAnimation from "../atoms/GifAnimation";
//import useScroll from "../atoms/useScroll";
import useDims from "../atoms/useDims";
import useIOSTouch from "../atoms/useIOSTouch";
import { sectionScale, checkIsPC, isIphone } from "../atoms";

import Navigation from "./components/Navigation";

import {
  BASE_URL,
  ENDPOINT_ABOUT,
  ENDPOINT_TEAMS,
  ENDPOINT_WORKS
} from "../../constants";

import WORK from "../../assets/noise/texts/MENU/WORK.gif";
import TEAM from "../../assets/noise/texts/MENU/TEAM.gif";
import ABOUT from "../../assets/noise/texts/MENU/ABOUT.gif";
import { ReactComponent as Close } from "../../assets/icons/close.svg";

const textGif = endpoint => {
  switch (endpoint) {
    case ENDPOINT_ABOUT:
      return ABOUT;
    case ENDPOINT_TEAMS:
      return TEAM;
    case ENDPOINT_WORKS:
      return WORK;
    default:
      return WORK;
  }
};

const ToggleLink = ({ to, children, onRevert, history, ...props }) => {
  const currentLoc = window.location.pathname;
  currentLoc === to && onRevert && onRevert();

  const handleIOSTouch = e => {
    if (currentLoc === to) {
      history && history.push("/");
    } else {
      history && history.push(to);
    }
  };

  const linkRef = useRef();
  useIOSTouch(linkRef, handleIOSTouch);

  return (
    <Link to={currentLoc === to ? "/" : to} innerRef={linkRef} {...props}>
      {children}
    </Link>
  );
};

const ButtonOverlay = ({ endpoint, ...props }) => {
  return (
    <ToggleLink to={endpoint} className={styles.link} {...props}>
      <div className={styles.buttonOverlay}> </div>
    </ToggleLink>
  );
};

const ExitBtn = ({ active }) => (
  <motion.button
    className={styles.exitBtn}
    initial={{ opacity: 0 }}
    animate={active ? "active" : "inactive"}
    variants={{
      active: { opacity: 1, transition: { delay: 1 } },
      inactive: { opacity: 0, transition: { delay: 0 } }
    }}
  >
    <Close className={styles.exitIcon} />
  </motion.button>
);

const contentVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { delay: 0.5 } }
};

const withSection = BaseComponent => ({
  endpoint,
  children,
  title,
  className,
  titleClassName,
  active,
  current,
  border,
  onScroll,
  onModal,
  workTabShowingModal,
  scrolling,
  dims,
  history,
  ...props
}) => {
  // FETCH
  // https://developer.wordpress.org/rest-api/using-the-rest-api/pagination/
  // https://developer.wordpress.org/rest-api/reference/posts/#list-posts
  const [state, doFetch] = useFetch(BASE_URL + endpoint + "?per_page=50", {});

  // CLEAN AND SORT DATA
  const hasData = state && state.data && state.data[0];
  const sortedData =
    (hasData &&
      state.data.sort(
        (a, b) => parseInt(a.acf.order) - parseInt(b.acf.order)
      )) ||
    [];

  // due to how the containers need to sit, this is needed to
  // toggle the navigation arrows on detail page (which sits here, at parent of detail page)
  const [isShowingDetail, setIsShowingDetail] = useState(false);

  const containerRef = useRef();
  const handleScrollToTop = () => {
    const el = containerRef.current;
    active && el.scrollTo(0, 0);
  };

  // content scaling according to width
  // js width
  const { width } = useDims({ height: 0, width: 0 });
  const isPC = checkIsPC(width);

  const scaledStyles = width => {
    const scaleOut = isPC ? sectionScale(width) : 1;
    const invScale = 1 / scaleOut;
    return {
      transform: `scale(${scaleOut})`,
      transformOrigin: `top left`,
      marginLeft: `calc(${1 - scaleOut} * 60px)`,
      marginTop: `calc(${1 - scaleOut} * 280px)`,
      marginBottom: 0,
      width: isPC ? `calc(66vw * ${invScale} - 120px * ${invScale})` : "100%"
    };
  };

  const handleModal = bool => {
    setIsShowingDetail(bool);
    onModal && onModal(bool);
  };

  const handleTabDisplay = () => {
    if (isPC) {
      return "block"; // pc always shows all tabs
    } else if (endpoint === "works") {
      return "block";
    } else if (workTabShowingModal) {
      return "none";
    } else {
      // console.log("\nactive endpoint", current);
      // console.log("this components endpoint", endpoint);
      // console.log("is local component active? ", active);
      // console.log("is local component showing carousel?", isShowingDetail);
      // console.log("is work component showing modal?", workTabShowingModal);

      return "block";
    }
  };

  const shouldRotate = () => {
    const sections = [ENDPOINT_WORKS, ENDPOINT_TEAMS, ENDPOINT_ABOUT];
    return !active && isPC && width <= 1300 && sections.indexOf(current) !== -1;
  };

  const scaled = scaledStyles(width);

  return (
    <motion.div
      ref={containerRef}
      className={`${styles.container} ${className}`}
      style={{
        display: handleTabDisplay(),
        ...dims
      }}
      {...props}
    >
      {!active && <ButtonOverlay endpoint={endpoint} history={history} />}

      <AnimatedTitleContainer
        className={styles.titleContainer}
        current={current}
        endpoint={endpoint}
        //style={{ width: dims.width, overflowX: "hidden" }}
      >
        <ToggleLink to={endpoint} history={history} className={styles.link}>
          <motion.h1
            className={`${styles.title} ${titleClassName}`}
            style={{ transformOrigin: "top left" }}
            animate={shouldRotate() ? "vertical" : "horizontal"}
            variants={{
              vertical: { rotate: 90, x: 91 + 60, y: -5 },
              horizontal: { rotate: 0, x: 0, y: 0 }
            }}
            transition={{
              duration: 0
            }}
            // transition={{
            //   type: "spring",
            //   stiffness: 1000,
            //   damping: 1000
            // }}
          >
            <GifAnimation
              gifSrc={textGif(endpoint)}
              className={styles.titleHeightFix}
            >
              {title.toUpperCase() || "subsection"}
            </GifAnimation>
          </motion.h1>
        </ToggleLink>

        {active && endpoint === ENDPOINT_WORKS && isShowingDetail && isPC && (
          <Navigation data={sortedData} />
        )}

        <ToggleLink to={endpoint} className={styles.link}>
          <ExitBtn active={active && isShowingDetail} />
        </ToggleLink>
      </AnimatedTitleContainer>

      <motion.div
        key={endpoint}
        //ref={ref}
        className={`${styles.contentContainer} ${active &&
          styles.contentActive}`}
        style={{ ...scaled }}
        variants={contentVariants}
        initial="hidden"
        animate={active ? "visible" : "hidden"}
      >
        <Route
          path={endpoint}
          render={({ ...rest }) => {
            // TODO: constant rerender? due to useScroll?
            //console.log("shit happening!", rest);

            // continuous scroll that switches sections
            const handleNextSection = url => {
              rest.location.pathname !== url && rest.history.push(url);
            };

            // ensure data exists
            if (hasData) {
              // ABOUT PAGE
              if (state.data.length === 1) {
                return (
                  <BaseComponent {...props} {...rest} data={state.data[0]} />
                );
              } else {
                // WORK and TEAMS page
                return (
                  <BaseComponent
                    onNextSection={handleNextSection}
                    onModal={handleModal}
                    {...props}
                    {...rest}
                    data={sortedData}
                    onScrollToTop={handleScrollToTop}
                  />
                );
              }
            } else {
              return (
                <div
                  className={styles.loadingPage}
                  style={{
                    height: "100vh",
                    width: "100%"
                    // backgroundImage: `url(${tabGif(endpoint)})`
                  }}
                />
              );
            }
          }}
        />
      </motion.div>
    </motion.div>
  );
};

export default withSection;
